import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import ReportCard from '@/components/base/ReportCard';
import { reports } from '@/mocks/reports';
import { categories } from '@/mocks/categories';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xodxd.cn/' },
    { '@type': 'ListItem', position: 2, name: '已核实报道', item: 'https://xodxd.cn/reports' },
  ],
};

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const activeCat = searchParams.get('cat') || 'all';

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      const matchCat = activeCat === 'all' || r.category === activeCat;
      const q = query.trim().toLowerCase();
      const matchQ = !q || r.title.toLowerCase().includes(q) || r.summary.toLowerCase().includes(q) || r.tags.some((t) => t.includes(q));
      return matchCat && matchQ;
    });
  }, [activeCat, query]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (query.trim()) params.q = query.trim();
    if (activeCat !== 'all') params.cat = activeCat;
    setSearchParams(params);
  };

  const setCat = (cat: string) => {
    const params: Record<string, string> = { cat };
    if (query.trim()) params.q = query.trim();
    setSearchParams(params);
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '已核实报道 - 17爆料',
    url: 'https://xodxd.cn/reports',
    description: '17爆料已核实并公开发布的线索报道归档，涵盖消费维权、食品安全、物业纠纷等多个栏目。',
    publisher: { '@id': 'https://xodxd.cn/#org' },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }} />
      <Navbar />
      <main className="pt-16">
        {/* Page hero */}
        <div className="bg-background-950 py-10">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-background-500 mb-4">
              <ol className="flex items-center gap-1.5 flex-wrap">
                <li><Link to="/" className="hover:text-background-300 transition-colors">首页</Link></li>
                <li><i className="ri-arrow-right-s-line" /></li>
                <li className="text-background-300">已核实报道</li>
              </ol>
            </nav>
            <h1 className="font-heading text-3xl font-black text-background-50 mb-2">已核实报道</h1>
            <p className="text-background-400 text-sm">所有报道均经编辑部多方核实后发布，共计 {reports.length} 篇</p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-sm" />
                </span>
                <input
                  type="text"
                  placeholder="搜索报道标题或关键词..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full text-sm pl-9 pr-3 py-2.5 border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          {/* Category filter */}
          <div className="flex gap-2 flex-wrap mb-8">
            <button
              onClick={() => setCat('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${activeCat === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
            >
              全部栏目
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCat(cat.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${activeCat === cat.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Results */}
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-foreground-400">
              <i className="ri-file-search-line text-4xl mb-3 block" />
              <p className="text-sm">暂无符合条件的报道</p>
              <button
                onClick={() => { setQuery(''); setCat('all'); }}
                className="mt-4 text-xs text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                清除筛选条件
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-foreground-500 mb-4">共找到 {filtered.length} 篇报道</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((r) => (
                  <ReportCard key={r.id} report={r} variant="default" />
                ))}
              </div>
            </>
          )}

          {/* Submit CTA */}
          <div className="mt-12 bg-primary-50 border border-primary-200 rounded-lg p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-bold text-foreground-900">没有找到你关注的报道？</h3>
              <p className="text-sm text-foreground-600 mt-1">提交你的线索，17爆料编辑部核实后将在此发布。</p>
            </div>
            <Link
              to="/submit"
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-md transition-colors whitespace-nowrap"
            >
              我要爆料
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}